import dev.shanty.kotwire.stimulus.Controller
import dev.shanty.kotwire.stimulus.StimulusController
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

@JsExport
@StimulusController(
    targets = ["input", "results", "backdrop", "icon", "spinner"],
    values = TripSearchController.Values::class,
)
class TripSearchController(context: dynamic) : Controller(context) {

    private val inputEl    by inputTarget<HTMLInputElement>()
    private val resultsEl  by resultsTarget<HTMLElement>()
    private val backdropEl by backdropTarget<HTMLElement>()
    private val iconEl     by iconTarget<HTMLElement>()
    private val spinnerEl  by spinnerTarget<HTMLElement>()
    private val searchUrl  by searchUrlValue()

    private var debounceHandle: Int = 0
    private var requestToken: Int = 0

    private var lastQuery: String = ""
    private var resultsValid: Boolean = false

    @JsName("onInput")
    fun onInput() {
        window.clearTimeout(debounceHandle)
        requestToken++
        val query = inputEl.value.trim()
        if (query.length < 2) {
            clearResults()
            return
        }
        if (query == lastQuery && resultsValid) {
            showResults()
            return
        }
        debounceHandle = window.setTimeout({ performSearch(query) }, 300)
    }

    @JsName("onFocus")
    fun onFocus() {
        val query = inputEl.value.trim()
        if (query.length >= 2 && query == lastQuery && resultsValid) {
            showResults()
        }
    }

    @JsName("close")
    fun close() {
        resultsEl.classList.add("hidden")
        backdropEl.classList.add("hidden")
    }

    private fun performSearch(query: String) {
        val token = ++requestToken
        showLoading()

        val url = "$searchUrl?q=${encodeURIComponent(query)}"
        window.asDynamic().fetch(url)
            .then { r: dynamic ->
                if (r.ok != true) throw Error("Search failed: ${r.status}")
                r.json()
            }
            .then { data: dynamic ->
                if (token == requestToken) {
                    renderResults(data)
                    lastQuery = query
                    resultsValid = true
                }
                null
            }
            .catch { err: dynamic ->
                console.error("Trip search failed", err)
                if (token == requestToken) clearResults()
                null
            }
            .then { _: dynamic ->
                if (token == requestToken) hideLoading()
                null
            }
    }

    private fun showLoading() {
        iconEl.classList.add("hidden")
        spinnerEl.classList.remove("hidden")
    }

    private fun hideLoading() {
        iconEl.classList.remove("hidden")
        spinnerEl.classList.add("hidden")
    }

    private fun renderResults(data: dynamic) {
        resultsEl.innerHTML = ""

        val results = data.results
        val count = (results.length as? Int) ?: 0

        if (count == 0) {
            val empty = document.createElement("p")
            empty.className = "px-4 py-3 text-sm text-gray-400 italic"
            empty.textContent = "No results found"
            resultsEl.appendChild(empty)
            showResults()
            return
        }

        for (i in 0 until count) {
            val result = results[i]
            val url = result.url as? String

            val row = if (url != null) {
                val a = document.createElement("a") as org.w3c.dom.HTMLAnchorElement
                a.href = url
                a
            } else {
                document.createElement("div")
            }
            row.className = "flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-default"

            val icon = document.createElement("span")
            icon.className = "text-base w-5 text-center shrink-0"
            icon.textContent = result.icon as String

            val textWrap = document.createElement("div")
            textWrap.className = "flex flex-col min-w-0"

            val summary = document.createElement("span")
            summary.className = "text-sm font-medium text-gray-800 truncate"
            summary.textContent = result.summary as String
            textWrap.appendChild(summary)

            val detail = result.detail as? String
            if (!detail.isNullOrBlank()) {
                val sub = document.createElement("span")
                sub.className = "text-xs text-gray-400 truncate"
                sub.textContent = detail
                textWrap.appendChild(sub)
            }

            row.appendChild(icon)
            row.appendChild(textWrap)
            resultsEl.appendChild(row)
        }

        showResults()
    }

    private fun showResults() {
        resultsEl.classList.remove("hidden")
        backdropEl.classList.remove("hidden")
    }

    private fun clearResults() {
        resultsEl.innerHTML = ""
        resultsEl.classList.add("hidden")
        backdropEl.classList.add("hidden")
        hideLoading()
        lastQuery = ""
        resultsValid = false
    }

    object Values {
        val searchUrl: String = ""
    }
}

private fun encodeURIComponent(value: String): String = js("encodeURIComponent(value)") as String
